import { z } from 'zod/v3';
import {
  EmployeeStatusSchema,
  LifecycleStatusSchema,
  EmployeeTypeSchema,
  EmployeeSourceSchema,
  ComplianceExclusionReasonSchema,
  TrainingStatusSchema,
  TrainingSubmissionMethodSchema,
  TrainingRecordSourceSchema,
  AccessReviewStatusSchema,
  AccessReviewItemDecisionSchema,
  DeviceComplianceStatusSchema,
  DeviceLifecycleStatusSchema,
  DeviceSecurityCheckKeySchema,
  DeviceSecurityCheckResultSchema,
  DeviceDisposalReasonSchema,
  DeviceDisposalMethodSchema,
  WorkspaceRoleSchema,
} from '../generated/enums';
import type {
  EmployeeStatus,
  LifecycleStatus,
  EmployeeType,
  ComplianceExclusionReason,
  TrainingStatus,
  TrainingSubmissionMethod,
  TrainingRecordSource,
  AccessReviewStatus,
  AccessReviewItemDecision,
  DeviceComplianceStatus,
  DeviceLifecycleStatus,
  DeviceSecurityCheckKey,
  DeviceSecurityCheckResult,
  DeviceDisposalReason,
  DeviceDisposalMethod,
} from '../generated/enums';

// Re-export enum schemas and types for convenience
export {
  EmployeeStatusSchema,
  LifecycleStatusSchema,
  EmployeeTypeSchema,
  ComplianceExclusionReasonSchema,
  TrainingStatusSchema,
  TrainingSubmissionMethodSchema,
  TrainingRecordSourceSchema,
  AccessReviewStatusSchema,
  AccessReviewItemDecisionSchema,
  DeviceComplianceStatusSchema,
  DeviceLifecycleStatusSchema,
  DeviceSecurityCheckKeySchema,
  DeviceSecurityCheckResultSchema,
  DeviceDisposalReasonSchema,
  DeviceDisposalMethodSchema,
};
export type {
  EmployeeStatus,
  LifecycleStatus,
  EmployeeType,
  ComplianceExclusionReason,
  TrainingStatus,
  TrainingSubmissionMethod,
  TrainingRecordSource,
  AccessReviewStatus,
  AccessReviewItemDecision,
  DeviceComplianceStatus,
  DeviceLifecycleStatus,
  DeviceSecurityCheckKey,
  DeviceSecurityCheckResult,
  DeviceDisposalReason,
  DeviceDisposalMethod,
};

export const BackgroundCheckStatusSchema = z.enum([
  'NOT_STARTED',
  'PENDING',
  'COMPLETED',
  'NOT_REQUIRED',
  'WAIVED',
]);
export type BackgroundCheckStatus = z.infer<typeof BackgroundCheckStatusSchema>;

// ===== Employee DTOs =====

export const EmployeeDTOSchema = z.object({
  id: z.string(),
  workspaceId: z.string(),

  /** Optional link to a platform User account */
  userId: z.string().nullable(),

  /** HR-assigned employee ID (e.g. "EMP-0042") */
  employeeId: z.string().nullable(),
  firstName: z.string(),
  lastName: z.string(),
  email: z.string(),
  /** Additional emails the employee uses across systems (Azure SSO, AWS IAM,
   * GitHub signup, etc.). Normalized via the same trim+NFC+lowercase rules
   * as `email`. Used by the RFC-180 M4 access-review reconciler so a single
   * person isn't classified as UNKNOWN across providers. */
  alternativeEmails: z.array(z.string()).default([]),
  title: z.string().nullable(),
  department: z.string().nullable(),
  manager: z.string().nullable(),
  managerEmployeeId: z.string().nullable(),
  location: z.string().nullable(),
  backgroundCheckStatus: BackgroundCheckStatusSchema.nullable(),
  backgroundCheckCompletedAt: z.coerce.date().nullable(),
  ndaSignedAt: z.coerce.date().nullable(),
  jobDescription: z.string().nullable(),

  type: EmployeeTypeSchema,
  status: EmployeeStatusSchema,
  /** RFC-199 — in-flight lifecycle process, orthogonal to `status`. Read the
   *  two axes together (e.g. `Active · Onboarding`). */
  lifecycleStatus: LifecycleStatusSchema,
  startDate: z.coerce.date().nullable(),
  endDate: z.coerce.date().nullable(),

  /** #969 directory provenance: how the row is sourced/maintained, which
   *  provider last synced it, and when. */
  source: EmployeeSourceSchema,
  sourceProvider: z.string().nullable(),
  syncedAt: z.coerce.date().nullable(),

  /** Whether the employee has an active self-service portal link */
  hasSelfServiceAccess: z.boolean(),
  lastSelfServiceAt: z.coerce.date().nullable(),

  /** RFC-180 M4 G8.3 — optional program-level role assignment that drives
   *  reconciler Rule 6 (role_mismatch). */
  roleId: z.string().nullable().optional(),

  metadata: z.record(z.unknown()).nullable(),

  /** Compliance-duty exclusion (service/test accounts etc.). A non-null
   *  `complianceExclusionReason` means the person is scoped out of coverage
   *  stats, training, policy-ack, and reports. `excludedAt`/`excludedBy` are
   *  stamped when the reason is first set and cleared when it's removed. */
  complianceExclusionReason: ComplianceExclusionReasonSchema.nullable(),
  complianceExclusionNote: z.string().nullable(),
  excludedAt: z.coerce.date().nullable(),
  excludedBy: z.string().nullable(),

  createdBy: z.string(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
});

export type EmployeeDTO = z.infer<typeof EmployeeDTOSchema>;

export const CreateEmployeeDTOSchema = z.object({
  employeeId: z.string().optional(),
  firstName: z.string().min(1).max(100),
  lastName: z.string().min(1).max(100),
  email: z.string().email(),
  alternativeEmails: z.array(z.string().email()).max(20).optional(),
  title: z.string().max(200).optional(),
  department: z.string().max(200).optional(),
  manager: z.string().max(200).optional(),
  managerEmployeeId: z.string().optional(),
  location: z.string().max(200).optional(),
  backgroundCheckStatus: BackgroundCheckStatusSchema.optional(),
  backgroundCheckCompletedAt: z.coerce.date().optional(),
  ndaSignedAt: z.coerce.date().optional(),
  jobDescription: z.string().max(4000).optional(),
  type: EmployeeTypeSchema.default('FULL_TIME'),
  status: EmployeeStatusSchema.default('ACTIVE'),
  startDate: z.coerce.date().optional(),
  endDate: z.coerce.date().optional(),
  /** Optional link to a platform User account */
  userId: z.string().optional(),
  metadata: z.record(z.unknown()).optional(),
});

export type CreateEmployeeDTO = z.infer<typeof CreateEmployeeDTOSchema>;

export const UpdateEmployeeDTOSchema = z.object({
  employeeId: z.string().optional(),
  firstName: z.string().min(1).max(100).optional(),
  lastName: z.string().min(1).max(100).optional(),
  email: z.string().email().optional(),
  alternativeEmails: z.array(z.string().email()).max(20).optional(),
  title: z.string().max(200).nullable().optional(),
  department: z.string().max(200).nullable().optional(),
  manager: z.string().max(200).nullable().optional(),
  managerEmployeeId: z.string().nullable().optional(),
  location: z.string().max(200).nullable().optional(),
  backgroundCheckStatus: BackgroundCheckStatusSchema.nullable().optional(),
  backgroundCheckCompletedAt: z.coerce.date().nullable().optional(),
  ndaSignedAt: z.coerce.date().nullable().optional(),
  jobDescription: z.string().max(4000).nullable().optional(),
  type: EmployeeTypeSchema.optional(),
  status: EmployeeStatusSchema.optional(),
  startDate: z.coerce.date().nullable().optional(),
  endDate: z.coerce.date().nullable().optional(),
  userId: z.string().nullable().optional(),
  /** RFC-180 M4 G8.3 — pick a RoleDefinition; null clears the assignment. */
  roleId: z.string().nullable().optional(),
  /** Compliance-duty exclusion. Set a reason to exclude, `null` to re-include.
   *  The service stamps/clears `excludedAt`/`excludedBy` from the reason. */
  complianceExclusionReason: ComplianceExclusionReasonSchema.nullable().optional(),
  complianceExclusionNote: z.string().max(2000).nullable().optional(),
  metadata: z.record(z.unknown()).nullable().optional(),
});

export type UpdateEmployeeDTO = z.infer<typeof UpdateEmployeeDTOSchema>;

/** Bulk edit of a shared attribute across selected employees (People-table
 *  header dropdowns). Kept to the fields that are safe to set uniformly —
 *  employment type + compliance exclusion. Per-row-only fields (name, email,
 *  role) are intentionally excluded. */
export const BulkUpdateEmployeesDTOSchema = z.object({
  ids: z.array(z.string()).min(1).max(500),
  data: z
    .object({
      type: EmployeeTypeSchema.optional(),
      complianceExclusionReason:
        ComplianceExclusionReasonSchema.nullable().optional(),
      complianceExclusionNote: z.string().max(2000).nullable().optional(),
    })
    .refine((d) => Object.keys(d).length > 0, {
      message: 'No fields to update',
    }),
});

export type BulkUpdateEmployeesDTO = z.infer<typeof BulkUpdateEmployeesDTOSchema>;

export const BulkDeleteEmployeesDTOSchema = z.object({
  ids: z.array(z.string().min(1)).min(1).max(500),
});

export type BulkDeleteEmployeesDTO = z.infer<typeof BulkDeleteEmployeesDTOSchema>;

export const BulkDeleteEmployeesResultDTOSchema = z.object({
  deletedIds: z.array(z.string()),
  failures: z.array(z.object({
    id: z.string(),
    message: z.string(),
  })),
});

export type BulkDeleteEmployeesResultDTO = z.infer<typeof BulkDeleteEmployeesResultDTOSchema>;

// ===== RoleDefinition DTOs (RFC-180 M4 G8.3) =====

export const RoleExpectedRolesByAppSchema = z.record(z.array(z.string()));

export const RoleDefinitionDTOSchema = z.object({
  id: z.string(),
  workspaceId: z.string(),
  programId: z.string(),
  name: z.string(),
  description: z.string().nullable(),
  expectedRolesByApp: RoleExpectedRolesByAppSchema,
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
  /** Optional count of employees assigned this role (from `_count.employees`). */
  employeeCount: z.number().int().optional(),
});
export type RoleDefinitionDTO = z.infer<typeof RoleDefinitionDTOSchema>;

export const CreateRoleDefinitionDTOSchema = z.object({
  programId: z.string(),
  name: z.string().min(1).max(120),
  description: z.string().max(2000).optional(),
  expectedRolesByApp: RoleExpectedRolesByAppSchema,
});
export type CreateRoleDefinitionDTO = z.infer<typeof CreateRoleDefinitionDTOSchema>;

export const UpdateRoleDefinitionDTOSchema = z.object({
  id: z.string(),
  name: z.string().min(1).max(120).optional(),
  description: z.string().nullable().optional(),
  expectedRolesByApp: RoleExpectedRolesByAppSchema.optional(),
});
export type UpdateRoleDefinitionDTO = z.infer<typeof UpdateRoleDefinitionDTOSchema>;

// ===== Employee Import DTOs =====

export const EmployeeImportRowResultSchema = z.object({
  rowIndex: z.number(),
  email: z.string(),
  action: z.enum(['created', 'updated', 'skipped', 'error']),
  employeeId: z.string().optional(),
  error: z.string().optional(),
});

export type EmployeeImportRowResult = z.infer<typeof EmployeeImportRowResultSchema>;

export const EmployeeImportResultDTOSchema = z.object({
  total: z.number(),
  created: z.number(),
  updated: z.number(),
  skipped: z.number(),
  errors: z.number(),
  rows: z.array(EmployeeImportRowResultSchema),
});

export type EmployeeImportResultDTO = z.infer<typeof EmployeeImportResultDTOSchema>;

// ===== Training Program DTOs =====

export const TrainingProgramDTOSchema = z.object({
  id: z.string(),
  workspaceId: z.string(),
  name: z.string(),
  description: z.string().nullable(),
  required: z.boolean(),
  /** Number of days from employee start date until training is due */
  dueDateDays: z.number().nullable(),
  /** Number of days until the training must be renewed */
  renewalDays: z.number().nullable(),
  createdBy: z.string(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
  assignedEmployeeIds: z.array(z.string()).nullable().optional(),
});

export type TrainingProgramDTO = z.infer<typeof TrainingProgramDTOSchema>;

export const CreateTrainingProgramDTOSchema = z.object({
  name: z.string().min(1).max(200),
  description: z.string().max(2000).optional(),
  required: z.boolean().default(true),
  dueDateDays: z.number().int().positive().optional(),
  renewalDays: z.number().int().positive().optional(),
});

export type CreateTrainingProgramDTO = z.infer<typeof CreateTrainingProgramDTOSchema>;

// ===== Training Record DTOs =====

export const TrainingRecordDTOSchema = z.object({
  id: z.string(),
  employeeId: z.string(),
  programId: z.string(),
  status: TrainingStatusSchema,
  submittedAt: z.coerce.date().nullable().optional(),
  submissionMethod: TrainingSubmissionMethodSchema.nullable().optional(),
  completedAt: z.coerce.date().nullable(),
  dueDate: z.coerce.date().nullable(),
  score: z.number().nullable(),
  certificateUrl: z.string().nullable(),
  reviewedAt: z.coerce.date().nullable().optional(),
  reviewedByUserId: z.string().nullable().optional(),
  reviewedByName: z.string().nullable().optional(),
  reviewComment: z.string().nullable().optional(),
  source: TrainingRecordSourceSchema.nullable().optional(),
  isAssigned: z.boolean(),
  recordedBy: z.string(),
  notes: z.string().nullable(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
  // Optional nested objects for display
  employee: EmployeeDTOSchema.pick({
    id: true,
    firstName: true,
    lastName: true,
    email: true,
    department: true,
    title: true,
  }).optional(),
  program: TrainingProgramDTOSchema.optional(),
});

export type TrainingRecordDTO = z.infer<typeof TrainingRecordDTOSchema>;

export const CreateTrainingRecordDTOSchema = z.object({
  employeeId: z.string(),
  programId: z.string(),
  status: TrainingStatusSchema.default('NOT_STARTED'),
  completedAt: z.coerce.date().optional(),
  dueDate: z.coerce.date().optional(),
  score: z.number().min(0).max(100).optional(),
  certificateUrl: z.string().url().optional(),
  notes: z.string().max(2000).optional(),
});

export type CreateTrainingRecordDTO = z.infer<typeof CreateTrainingRecordDTOSchema>;

export const BulkAssignTrainingProgramsDTOSchema = z.object({
  programIds: z.array(z.string()).min(1),
  employeeIds: z.array(z.string()).min(1),
});

export type BulkAssignTrainingProgramsDTO = z.infer<
  typeof BulkAssignTrainingProgramsDTOSchema
>;

export const SubmitMyTrainingCompletionDTOSchema = z.object({
  recordId: z.string(),
  completedAt: z.coerce.date().optional(),
  score: z.number().min(0).max(100).optional(),
  certificateUrl: z.string().url().optional(),
  notes: z.string().max(2000).optional(),
  submissionMethod: TrainingSubmissionMethodSchema.optional(),
});

export type SubmitMyTrainingCompletionDTO = z.infer<
  typeof SubmitMyTrainingCompletionDTOSchema
>;

export const ReviewTrainingSubmissionDecisionSchema = z.enum([
  'APPROVE',
  'REQUEST_CHANGES',
]);

export type ReviewTrainingSubmissionDecision = z.infer<
  typeof ReviewTrainingSubmissionDecisionSchema
>;

export const ReviewTrainingSubmissionDTOSchema = z.object({
  recordId: z.string(),
  decision: ReviewTrainingSubmissionDecisionSchema,
  comment: z.string().max(2000).optional(),
});

export type ReviewTrainingSubmissionDTO = z.infer<
  typeof ReviewTrainingSubmissionDTOSchema
>;

// ===== Access Review DTOs =====

/// RFC-227 — population collection can fail (dead integration credentials,
/// unsupported provider, snapshot never completed). Persisted on the review's
/// `scopeConfig` so the UI can render a failure instead of an endless
/// "Collecting…" spinner, and cleared as soon as a retry populates the review.
export const AccessReviewPopulationErrorSchema = z.object({
  message: z.string(),
  provider: z.string().nullable(),
  failedAt: z.string().datetime(),
});
export type AccessReviewPopulationError = z.infer<
  typeof AccessReviewPopulationErrorSchema
>;

/// RFC-227 — how an application review collects its accounts. Nothing is
/// collected at launch; the reviewer picks one method per application.
export const AccessReviewPopulationMethodSchema = z.enum([
  'SYNC',
  'IMPORT',
  'SCREENSHOT',
]);
export type AccessReviewPopulationMethod = z.infer<
  typeof AccessReviewPopulationMethodSchema
>;

export const AccessReviewPopulationStateSchema = z.enum([
  'NOT_STARTED',
  'COLLECTING',
  'FAILED',
  'POPULATED',
]);
export type AccessReviewPopulationState = z.infer<
  typeof AccessReviewPopulationStateSchema
>;

export const AccessReviewDTOSchema = z.object({
  editing: z.boolean().optional(),
  evidenceCleanupPending: z.boolean().optional(),
  id: z.string(),
  workspaceId: z.string(),
  name: z.string(),
  description: z.string().nullable(),
  reviewDate: z.coerce.date(),
  dueDate: z.coerce.date(),
  status: AccessReviewStatusSchema,
  controlEvidenceStatusId: z.string().optional(),
  periodKey: z.string().nullable().optional(),
  applicationKey: z.string().nullable().optional(),
  applicationRevision: z.number().int().positive().optional(),
  scopeConfig: z.unknown().nullable().optional(),
  scopeType: z.enum(['INTEGRATION', 'CUSTOM', 'ASSET']).nullable().optional(),
  scopeId: z.string().nullable().optional(),
  scopeName: z.string().nullable().optional(),
  evidencePackId: z.string().nullable().optional(),
  evidencePackSignatureStatus: z.string().nullable().optional(),
  reviewerUserId: z.string().nullable().optional(),
  reviewerName: z.string().nullable().optional(),
  populationRunId: z.string().nullable().optional(),
  populationReconciledAt: z.coerce.date().nullable().optional(),
  populationError: AccessReviewPopulationErrorSchema.nullable().optional(),
  /** Where this review is in collecting its accounts (derived server-side). */
  populationState: AccessReviewPopulationStateSchema.optional(),
  /** Collection methods the reviewer may still choose for this application. */
  populationMethods: z.array(AccessReviewPopulationMethodSchema).optional(),
  /** The method that produced the current accounts, once collected. */
  populationMethod: AccessReviewPopulationMethodSchema.nullable().optional(),
  completedAt: z.coerce.date().nullable(),
  createdBy: z.string(),
  completedBy: z.string().nullable(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
  /** Total number of access items in this review */
  itemCount: z.number().optional(),
  /** Number of items with a final decision */
  resolvedCount: z.number().optional(),
  /** Number of denied or modified desired-state changes */
  deniedCount: z.number().optional(),
  /** How many of `deniedCount` have had their removal recorded (RFC-227). */
  remediatedCount: z.number().optional(),
  /**
   * Whether this application's period is signed. Remediation stays recordable
   * on a COMPLETED application, so the UI gates on this rather than on
   * `status`, which locks earlier.
   */
  periodSigned: z.boolean().optional(),
});

export type AccessReviewDTO = z.infer<typeof AccessReviewDTOSchema>;

export const AccessReviewCoverageEntrySchema = z.object({
  systemKey: z.string(),
  name: z.string(),
  category: z.enum(['AUTO_POPULATED', 'MANUAL', 'OUT_OF_SCOPE']),
  provider: z.string().optional(),
  reason: z.string().optional(),
  grantCount: z.number().int().nonnegative().optional(),
  scanRunId: z.string().optional(),
  scannedAt: z.string().datetime().optional(),
});
export type AccessReviewCoverageEntry = z.infer<
  typeof AccessReviewCoverageEntrySchema
>;

export const AccessReviewPeriodStatusSchema = z.enum([
  'OPEN',
  'READY_TO_SIGN',
  'PENDING_APPROVAL',
  'SIGNED',
]);

export const AccessReviewPeriodDTOSchema = z.object({
  id: z.string().nullable(),
  revisionNumber: z.number().int(),
  latestRevisionPeriodKey: z.string(),
  revisionHistory: z.array(z.object({
    periodKey: z.string(),
    revisionNumber: z.number().int(),
    status: AccessReviewPeriodStatusSchema,
    signedAt: z.coerce.date().nullable(),
  })),
  periodKey: z.string(),
  label: z.string(),
  status: AccessReviewPeriodStatusSchema,
  controlEvidenceStatusId: z.string(),
  anchorTestCode: z.string().nullable(),
  anchorTestTitle: z.string().nullable(),
  anchorTestStatus: z.string(),
  anchorSubmissionStatus: z.string().nullable(),
  auditCycleId: z.string().nullable(),
  auditCycleLabel: z.string().nullable(),
  nextReviewDueAt: z.coerce.date().nullable(),
  reviewDate: z.coerce.date(),
  dueDate: z.coerce.date(),
  defaultReviewerUserId: z.string().nullable(),
  /** Period reviewer (defaults to the owner). */
  reviewerName: z.string().nullable(),
  reviewerEmail: z.string().nullable(),
  /** Who started the review (owner) and when. */
  createdBy: z.string(),
  createdByName: z.string().nullable(),
  createdAt: z.coerce.date(),
  /** Who signed the review and when; null until signed. */
  signedByName: z.string().nullable(),
  signedAt: z.coerce.date().nullable(),
  /** Assigned approver; a historical designation alone is not an approval. */
  approvedByUserId: z.string().nullable(),
  approvedByName: z.string().nullable(),
  approvalRequestedAt: z.coerce.date().nullable(),
  approvalRequestedBy: z.string().nullable(),
  approvalRequestedByName: z.string().nullable(),
  approvalReviewedAt: z.coerce.date().nullable(),
  approvalDecision: z.enum(['APPROVED', 'REJECTED', 'WITHDRAWN']).nullable(),
  approvalNotes: z.string().nullable(),
  approvalAttestationExceptions: z.string().nullable(),
  /** Its packs are linked in the Test's Evidence section. */
  evidenceLinked: z.boolean(),
  applicationCount: z.number().int(),
  completedApplicationCount: z.number().int(),
  itemCount: z.number().int(),
  resolvedCount: z.number().int(),
  deniedCount: z.number().int(),
  /**
   * Denied / modified grants — the changes the reviewer applies in the
   * application. Screenata records them; it does not execute them.
   */
  desiredStateChanges: z.array(
    z.object({
      id: z.string(),
      account: z.string(),
      application: z.string(),
      decision: z.string(),
    }),
  ),
  workforceSnapshotFileKey: z.string().nullable(),
  periodEvidencePackId: z.string().nullable(),
  periodEvidencePackSignatureStatus: z.string().nullable(),
  applicationReviews: z.array(AccessReviewDTOSchema),
  coverageDisclosure: z.array(AccessReviewCoverageEntrySchema),
});

export type AccessReviewPeriodDTO = z.infer<typeof AccessReviewPeriodDTOSchema>;

// ===== RFC-180 M4 — extended AR types =====

/// Reconciler recommendation. Aligned with claude-grc-engineering's
/// access-review-triage skill (`auto-certify` / `manager-confirm` /
/// `revoke (suggested)` / `investigate`). The reconciler outputs one of
/// these per item; the reviewer ultimately records a decision from
/// `AccessReviewItemDecision` (APPROVED / REVOKED / MODIFIED).
///
/// Why the distinction between INVESTIGATE and MANAGER_CONFIRM matters:
/// INVESTIGATE means "alarming — security team should look" (terminated,
/// SoD conflict, dormant admin, service account in human review).
/// MANAGER_CONFIRM means "plausible but suspicious — direct manager owns
/// it" (privileged access, role mismatch, new since last cycle, default).
/// Auditors want both surfaces; bucketing all of these as INVESTIGATE
/// loses the routing distinction.
export const AccessReviewRecommendationSchema = z.enum([
  'CERTIFY',
  'REVOKE',
  'INVESTIGATE',
  'MANAGER_CONFIRM',
  'OUT_OF_SCOPE',
]);
export type AccessReviewRecommendation = z.infer<
  typeof AccessReviewRecommendationSchema
>;

/// Item priority — drives ordering on the cycle detail page.
export const AccessReviewItemPrioritySchema = z.enum(['P0', 'P1', 'P2']);
export type AccessReviewItemPriority = z.infer<
  typeof AccessReviewItemPrioritySchema
>;

/// Principal-type discriminator (set per item).
export const AccessReviewPrincipalTypeSchema = z.enum([
  'EMPLOYEE',
  'SERVICE_ACCOUNT',
  'UNKNOWN',
]);
export type AccessReviewPrincipalType = z.infer<
  typeof AccessReviewPrincipalTypeSchema
>;

/// 'INTEGRATION' (auto-reconciled) vs 'CUSTOM' (admin-attested) item.
export const AccessReviewApplicationSourceSchema = z.enum([
  'INTEGRATION',
  'CUSTOM',
  'LICENSE',
]);
export type AccessReviewApplicationSource = z.infer<
  typeof AccessReviewApplicationSourceSchema
>;

/// Per-item review mode.
export const AccessReviewModeSchema = z.enum([
  'PER_PRINCIPAL',
  'BULK_ATTESTED',
]);
export type AccessReviewMode = z.infer<typeof AccessReviewModeSchema>;

export const AccessReviewPrivilegeTierSchema = z.enum([
  'ADMIN',
  'USER',
  'VIEWER',
  'UNKNOWN',
]);
export type AccessReviewPrivilegeTier = z.infer<
  typeof AccessReviewPrivilegeTierSchema
>;

export const AccessReviewPrivilegeTierSourceSchema = z.enum([
  'PROVIDER_MAP',
  'NAME_HEURISTIC',
  'DEFAULT',
  'OVERRIDE',
  'FALLBACK',
]);
export type AccessReviewPrivilegeTierSource = z.infer<
  typeof AccessReviewPrivilegeTierSourceSchema
>;

export const AccessReviewScopeConfigSchema = z.object({
  version: z.literal(1),
  /** Durable retry state for unsigned application evidence cleanup. */
  pendingCleanupKeys: z.array(z.string()).optional(),
  generationKeys: z.record(z.string()).optional(),
  deletionRequested: z.boolean().optional(),
  editing: z.boolean().optional(),
  recompleteBy: z.string().optional(),
  identityOverrides: z.record(z.string()).optional(),
  populationError: AccessReviewPopulationErrorSchema.nullable().optional(),
  /** Set when a sync is requested; cleared when the job succeeds or fails. */
  populationRequestedAt: z.string().datetime().nullable().optional(),
  populationMethod: AccessReviewPopulationMethodSchema.nullable().optional(),
  /** Original import/screenshot retained as review input until final approval. */
  populationSourceFileId: z.string().optional(),
  /** Immutable references to legacy item uploads inherited by a revision. */
  revisionSourceFiles: z.array(z.object({
    id: z.string(),
    filename: z.string(),
    url: z.string(),
    mimeType: z.string(),
  })).optional(),
  populationImport: z.object({
    accountCount: z.number(),
    grantCount: z.number(),
    duplicateWarnings: z.array(z.string()),
    columnMapping: z.record(z.string()),
    comparisonWarning: z.string().optional(),
  }).optional(),
  /** Server-side OCR preview; promoted to populationSourceFileId on import. */
  populationSourcePreview: z
    .object({
      fileId: z.string(),
      headers: z.array(z.string()),
      rows: z.array(z.record(z.string())),
      confidence: z.number(),
      warnings: z.array(z.string()),
      previewedAt: z.string().datetime(),
      previewedBy: z.string(),
    })
    .optional(),
  entitlementTierOverrides: z
    .record(AccessReviewPrivilegeTierSchema)
    .optional(),
  entitlementReviewerUserIds: z.record(z.string()).optional(),
  entitlementsConfirmedAt: z.string().datetime().optional(),
  entitlementsConfirmedBy: z.string().optional(),
  coverageDisclosure: z
    .array(AccessReviewCoverageEntrySchema)
    .optional(),
});
export type AccessReviewScopeConfig = z.infer<
  typeof AccessReviewScopeConfigSchema
>;

export const AccessReviewEntitlementDTOSchema = z.object({
  key: z.string(),
  externalId: z.string().nullable(),
  name: z.string(),
  privilegeTier: AccessReviewPrivilegeTierSchema,
  privilegeTierSource: AccessReviewPrivilegeTierSourceSchema,
  assignedReviewerUserId: z.string().nullable(),
  assignedReviewerName: z.string().nullable(),
  grantCount: z.number().int().nonnegative(),
});
export type AccessReviewEntitlementDTO = z.infer<
  typeof AccessReviewEntitlementDTOSchema
>;

export const AccessReviewEntitlementConfigurationDTOSchema = z.object({
  confirmedAt: z.string().datetime().nullable(),
  confirmedBy: z.string().nullable(),
  entitlements: z.array(AccessReviewEntitlementDTOSchema),
});
export type AccessReviewEntitlementConfigurationDTO = z.infer<
  typeof AccessReviewEntitlementConfigurationDTOSchema
>;

/// Subset of structured change types the admin can check off on the
/// Mark-Reviewed flow. Matches RFC-180 §6.1.
export const AttestedChangeTypeSchema = z.enum([
  'new_hire',
  'termination',
  'role_change',
  'permission_change',
  'deactivation',
  'no_change',
  'other',
]);
export type AttestedChangeType = z.infer<typeof AttestedChangeTypeSchema>;

/// CustomApplication — admin-registered app reviewed in AR cycles.
export const CustomApplicationDTOSchema = z.object({
  id: z.string(),
  workspaceId: z.string(),
  programId: z.string(),
  name: z.string(),
  description: z.string().nullable(),
  ownerRoleId: z.string().nullable(),
  ownerRole: z
    .object({ id: z.string(), name: z.string() })
    .nullable()
    .optional(),
  isActive: z.boolean(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type CustomApplicationDTO = z.infer<typeof CustomApplicationDTOSchema>;

export const CreateCustomApplicationDTOSchema = z.object({
  name: z.string().min(1).max(200),
  description: z.string().max(2000).optional(),
  ownerRoleId: z.string().optional(),
});
export type CreateCustomApplicationDTO = z.infer<
  typeof CreateCustomApplicationDTOSchema
>;

// ===== Access Review Item DTOs =====

export const AccessReviewItemDTOSchema = z.object({
  id: z.string(),
  reviewId: z.string(),
  /** Nullable since RFC-180 M4 — service accounts / unknowns don't carry an Employee row. */
  employeeId: z.string().nullable(),
  systemName: z.string(),
  accessLevel: z.string(),
  decision: AccessReviewItemDecisionSchema,
  reviewedBy: z.string().nullable(),
  reviewedAt: z.coerce.date().nullable(),
  notes: z.string().nullable(),
  /**
   * RFC-227 remediation record. Only meaningful on a denied grant: a null
   * `remediatedAt` there means the access has not been reported as removed
   * yet. Screenata never applies the change itself.
   */
  remediatedAt: z.coerce.date().nullable().optional(),
  remediatedBy: z.string().nullable().optional(),
  remediationNotes: z.string().nullable().optional(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),

  // RFC-180 M4 — reviewer routing + reconciler output + change detection.
  principalType: AccessReviewPrincipalTypeSchema.optional(),
  principalExternalId: z.string().nullable().optional(),
  principalDisplayName: z.string().nullable().optional(),
  principalEmail: z.string().nullable().optional(),
  applicationSource: AccessReviewApplicationSourceSchema.optional(),
  integrationId: z.string().nullable().optional(),
  customApplicationId: z.string().nullable().optional(),
  reviewMode: AccessReviewModeSchema.optional(),
  recommendation: AccessReviewRecommendationSchema.nullable().optional(),
  recommendationReason: z.string().nullable().optional(),
  priority: AccessReviewItemPrioritySchema.nullable().optional(),
  ruleMatched: z.string().nullable().optional(),
  populationRunId: z.string().nullable().optional(),
  entitlementExternalId: z.string().nullable().optional(),
  entitlementName: z.string().nullable().optional(),
  privilegeTier: AccessReviewPrivilegeTierSchema.nullable().optional(),
  privilegeTierSource:
    AccessReviewPrivilegeTierSourceSchema.nullable().optional(),
  assignedReviewerUserId: z.string().nullable().optional(),
  assignedComplianceRoleId: z.string().nullable().optional(),

  // BULK_ATTESTED fields.
  attestedUserCount: z.number().int().nullable().optional(),
  attestedSummary: z.string().nullable().optional(),
  attestedChangeTypes: z.array(AttestedChangeTypeSchema).optional(),

  // RFC-180 UX-20 — raw last-seen timestamp from the provider; the UI
  // derives "Dormant >90d" at render time so the rule label doesn't bake
  // an interpretation into the data.
  lastActiveAt: z.coerce.date().nullable().optional(),

  // Change-detection vs prior cycle.
  detectedChangeSinceLastCycle: z.string().nullable().optional(),
  detectedChangeDetailJson: z.unknown().nullable().optional(),

  // Optional nested objects for display
  employee: EmployeeDTOSchema.pick({
    id: true,
    firstName: true,
    lastName: true,
    email: true,
    department: true,
    title: true,
    complianceExclusionReason: true,
  }).optional(),

  /// RFC-180 SA-1 — when populated, the principal is in the workspace's
  /// service-account registry. The UI uses this to swap the decision
  /// verb ("Owner confirms still needed?" instead of "Certify access?")
  /// and surface owner + purpose inline. Null = not registered.
  serviceAccount: z.object({
    id: z.string(),
    ownerEmployeeId: z.string(),
    ownerName: z.string(),
    purpose: z.string(),
    isTestAccount: z.boolean(),
    lastAttestedAt: z.coerce.date().nullable(),
  }).nullable().optional(),
});

export type AccessReviewItemDTO = z.infer<typeof AccessReviewItemDTOSchema>;

export const UpdateAccessReviewItemDTOSchema = z.object({
  decision: AccessReviewItemDecisionSchema,
  notes: z.string().max(2000).optional(),
});

export type UpdateAccessReviewItemDTO = z.infer<typeof UpdateAccessReviewItemDTOSchema>;

// ===== Service Account Registry DTOs (RFC-180 SA-1) =====

/// Owner info inlined into the registry DTO so the UI doesn't need a
/// second round-trip. Pick the minimum needed for the row label /
/// dropdown — keeps the payload small.
export const ServiceAccountRegistryDTOSchema = z.object({
  id: z.string(),
  workspaceId: z.string(),
  systemName: z.string(),
  principalExternalId: z.string(),
  principalDisplayName: z.string().nullable(),
  principalEmail: z.string().nullable(),
  ownerEmployeeId: z.string(),
  purpose: z.string(),
  isTestAccount: z.boolean(),
  taggedBy: z.string().nullable(),
  taggedAt: z.coerce.date(),
  lastAttestedAt: z.coerce.date().nullable(),
  lastAttestedBy: z.string().nullable(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
  /// Inlined owner for display — same minimal shape as AR item.
  owner: EmployeeDTOSchema.pick({
    id: true,
    firstName: true,
    lastName: true,
    email: true,
    department: true,
    title: true,
  }).optional(),
});

export type ServiceAccountRegistryDTO = z.infer<typeof ServiceAccountRegistryDTOSchema>;

/// Used by the "Mark as service account" dialog. `systemName` +
/// `principalExternalId` together are the registry's natural key.
export const MarkServiceAccountDTOSchema = z.object({
  systemName: z.string().min(1),
  principalExternalId: z.string().min(1),
  principalDisplayName: z.string().nullable().optional(),
  principalEmail: z.string().nullable().optional(),
  ownerEmployeeId: z.string().min(1),
  purpose: z.string().min(1).max(2000),
  isTestAccount: z.boolean().default(false),
});

export type MarkServiceAccountDTO = z.infer<typeof MarkServiceAccountDTOSchema>;

/// Bulk variant used from the access-review table: one owner, purpose and
/// test flag applied to every selected principal. Same registry rows as
/// `mark`, just fewer clicks when a tenant has a batch of shared mailboxes
/// or sandbox users.
export const MarkServiceAccountsDTOSchema = z.object({
  principals: z
    .array(
      MarkServiceAccountDTOSchema.pick({
        systemName: true,
        principalExternalId: true,
        principalDisplayName: true,
        principalEmail: true,
      }),
    )
    .min(1)
    .max(200),
  ownerEmployeeId: z.string().min(1),
  purpose: z.string().min(1).max(2000),
  isTestAccount: z.boolean().default(false),
});

export type MarkServiceAccountsDTO = z.infer<typeof MarkServiceAccountsDTOSchema>;

// ===== Employee Device DTOs =====

/** Corporate-owned vs personal (BYOD) device — decides which endpoint
 * controls apply (MDM/encryption enforcement vs BYOD policy terms). */
export const DEVICE_OWNERSHIP = ['COMPANY', 'BYOD'] as const;
export const DeviceOwnershipSchema = z.enum(DEVICE_OWNERSHIP);
export type DeviceOwnership = z.infer<typeof DeviceOwnershipSchema>;

export const EmployeeDeviceDTOSchema = z.object({
  id: z.string(),
  /** Null for unclaimed devices (e.g. MDM-synced with no matching employee). */
  employeeId: z.string().nullable(),
  workspaceId: z.string(),
  /** RFC-211 — 1:1 link to the canonical Asset(DEVICE) spine. Null until the
   *  write path backfills it (legacy/manual rows). */
  assetId: z.string().nullable(),
  serialNumber: z.string().nullable(),
  name: z.string(),
  type: z.string(),
  /** "COMPANY" | "BYOD"; null = unknown (e.g. MDM sync without ownership). */
  ownership: DeviceOwnershipSchema.nullable(),
  manufacturer: z.string().nullable(),
  model: z.string().nullable(),
  osName: z.string().nullable(),
  osVersion: z.string().nullable(),
  complianceStatus: DeviceComplianceStatusSchema,
  /** #1449 — operational lifecycle, orthogonal to complianceStatus. */
  lifecycleStatus: DeviceLifecycleStatusSchema,
  /** Retire candidate: last successful MDM scan no longer reported this device. */
  missingFromMdmSince: z.coerce.date().nullable(),
  purchaseDate: z.coerce.date().nullable(),
  warrantyExpiresAt: z.coerce.date().nullable(),
  encryptionEnabled: z.boolean().nullable(),
  firewallEnabled: z.boolean().nullable(),
  antivirusEnabled: z.boolean().nullable(),
  autoUpdateEnabled: z.boolean().nullable(),
  lastCheckIn: z.coerce.date().nullable(),
  mdmProvider: z.string().nullable(),
  mdmDeviceId: z.string().nullable(),
  mdmMetadata: z.record(z.unknown()).nullable(),
  createdBy: z.string(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
  // Optional nested employee for display
  employee: EmployeeDTOSchema.pick({
    id: true,
    firstName: true,
    lastName: true,
    email: true,
    department: true,
    title: true,
    status: true,
  }).optional(),
});

export type EmployeeDeviceDTO = z.infer<typeof EmployeeDeviceDTOSchema>;

export const DeviceSecurityCheckObservationDTOSchema = z.object({
  id: z.string(),
  deviceId: z.string(),
  checkKey: DeviceSecurityCheckKeySchema,
  result: DeviceSecurityCheckResultSchema,
  source: z.literal('MANUAL_SCREENSHOT'),
  observedAt: z.coerce.date(),
  submittedBy: z.string(),
  submittedByName: z.string().nullable(),
  artifactUrl: z.string(),
  filename: z.string(),
  mimeType: z.string(),
  size: z.number().int().nonnegative(),
  contentHash: z.string(),
  createdAt: z.coerce.date(),
});
export type DeviceSecurityCheckObservationDTO = z.infer<
  typeof DeviceSecurityCheckObservationDTOSchema
>;

export const ResolvedDeviceSecurityCheckDTOSchema = z.object({
  deviceId: z.string(),
  checkKey: DeviceSecurityCheckKeySchema,
  status: z.enum(['PASS', 'FAIL', 'MISSING', 'STALE']),
  source: z.enum(['OPEN_ATTEST', 'AUTOMATIC', 'MANUAL_SCREENSHOT']).nullable(),
  observedAt: z.coerce.date().nullable(),
  automaticStatus: z.enum(['PASS', 'FAIL', 'MISSING', 'STALE']),
  automaticSource: z.enum(['OPEN_ATTEST', 'AUTOMATIC']).nullable(),
  automaticObservedAt: z.coerce.date().nullable(),
  manualEvidence: DeviceSecurityCheckObservationDTOSchema.nullable(),
});
export type ResolvedDeviceSecurityCheckDTO = z.infer<
  typeof ResolvedDeviceSecurityCheckDTOSchema
>;

export const UploadDeviceSecurityCheckEvidenceDTOSchema = z.object({
  deviceId: z.string().min(1),
  checkKey: DeviceSecurityCheckKeySchema,
  result: DeviceSecurityCheckResultSchema,
  observedAt: z.coerce.date(),
  filename: z.string().min(1).max(255),
  contentType: z.string().min(1).max(200),
  base64Data: z.string().min(1),
  size: z.number().int().nonnegative(),
});
export type UploadDeviceSecurityCheckEvidenceDTO = z.infer<
  typeof UploadDeviceSecurityCheckEvidenceDTOSchema
>;

export const CreateEmployeeDeviceDTOSchema = z.object({
  employeeId: z.string(),
  serialNumber: z.string().max(200).optional(),
  name: z.string().min(1).max(200),
  type: z.string().min(1).max(100),
  ownership: DeviceOwnershipSchema.optional(),
  manufacturer: z.string().max(100).optional(),
  model: z.string().max(200).optional(),
  osName: z.string().max(100).optional(),
  osVersion: z.string().max(100).optional(),
  complianceStatus: DeviceComplianceStatusSchema.default('UNKNOWN'),
  lifecycleStatus: DeviceLifecycleStatusSchema.optional(),
  purchaseDate: z.coerce.date().optional(),
  warrantyExpiresAt: z.coerce.date().optional(),
  encryptionEnabled: z.boolean().optional(),
  firewallEnabled: z.boolean().optional(),
  antivirusEnabled: z.boolean().optional(),
  autoUpdateEnabled: z.boolean().optional(),
  lastCheckIn: z.coerce.date().optional(),
  mdmProvider: z.string().max(100).optional(),
  mdmDeviceId: z.string().max(200).optional(),
  mdmMetadata: z.record(z.unknown()).optional(),
});

export type CreateEmployeeDeviceDTO = z.infer<typeof CreateEmployeeDeviceDTOSchema>;

export const UpdateEmployeeDeviceDTOSchema = z.object({
  employeeId: z.string().nullable().optional(),
  serialNumber: z.string().max(200).nullable().optional(),
  name: z.string().min(1).max(200).optional(),
  type: z.string().min(1).max(100).optional(),
  ownership: DeviceOwnershipSchema.nullable().optional(),
  manufacturer: z.string().max(100).nullable().optional(),
  model: z.string().max(200).nullable().optional(),
  osName: z.string().max(100).nullable().optional(),
  osVersion: z.string().max(100).nullable().optional(),
  complianceStatus: DeviceComplianceStatusSchema.optional(),
  lifecycleStatus: DeviceLifecycleStatusSchema.optional(),
  purchaseDate: z.coerce.date().nullable().optional(),
  warrantyExpiresAt: z.coerce.date().nullable().optional(),
  encryptionEnabled: z.boolean().nullable().optional(),
  firewallEnabled: z.boolean().nullable().optional(),
  antivirusEnabled: z.boolean().nullable().optional(),
  autoUpdateEnabled: z.boolean().nullable().optional(),
  lastCheckIn: z.coerce.date().nullable().optional(),
  mdmProvider: z.string().max(100).nullable().optional(),
  mdmDeviceId: z.string().max(200).nullable().optional(),
  mdmMetadata: z.record(z.unknown()).nullable().optional(),
});

export type UpdateEmployeeDeviceDTO = z.infer<typeof UpdateEmployeeDeviceDTOSchema>;

/**
 * RFC-227 D7 — one custody period of a device. `returnedAt === null` is the
 * current holder. `source` is 'manual', an MDM provider slug, or 'backfill'
 * (seeded from the current assignment; `assignedAt` is approximate).
 */
export const DeviceAssignmentRecordDTOSchema = z.object({
  id: z.string(),
  deviceId: z.string(),
  employeeId: z.string(),
  assignedAt: z.coerce.date(),
  returnedAt: z.coerce.date().nullable(),
  source: z.string(),
  notes: z.string().nullable(),
  employee: EmployeeDTOSchema.pick({
    id: true,
    firstName: true,
    lastName: true,
    email: true,
  }),
});

export type DeviceAssignmentRecordDTO = z.infer<typeof DeviceAssignmentRecordDTOSchema>;

/**
 * DCF-109 — a wipe or destruction of a device's data, on retirement or on
 * return before reassignment. Feeds the generated disposal log.
 */
export const DeviceDisposalRecordDTOSchema = z.object({
  id: z.string(),
  deviceId: z.string().nullable(),
  deviceName: z.string(),
  serialNumber: z.string().nullable(),
  reason: DeviceDisposalReasonSchema,
  method: DeviceDisposalMethodSchema,
  details: z.string().nullable(),
  disposedAt: z.coerce.date(),
  recordedBy: z.string(),
  createdAt: z.coerce.date(),
});

export type DeviceDisposalRecordDTO = z.infer<typeof DeviceDisposalRecordDTOSchema>;

export const RecordDeviceDisposalDTOSchema = z.object({
  deviceId: z.string(),
  reason: DeviceDisposalReasonSchema,
  method: DeviceDisposalMethodSchema,
  details: z.string().max(2000).nullish(),
  disposedAt: z.coerce.date(),
});

export type RecordDeviceDisposalDTO = z.infer<typeof RecordDeviceDisposalDTOSchema>;

// ===== Employee Acknowledgment DTOs =====

export const EmployeeAcknowledgmentDTOSchema = z.object({
  id: z.string(),
  employeeId: z.string(),
  templateId: z.string(),
  templateVersionId: z.string().nullable().optional(),
  templateTitle: z.string().nullable().optional(),
  templateTitleSnapshot: z.string().nullable().optional(),
  version: z.number(),
  acknowledgedAt: z.coerce.date(),
  ipAddress: z.string().nullable(),
  userAgent: z.string().nullable(),
  /** Acknowledgment method (e.g., "WEB", "EMAIL", "SELF_SERVICE") */
  method: z.string(),
});

export type EmployeeAcknowledgmentDTO = z.infer<typeof EmployeeAcknowledgmentDTOSchema>;

export const EmployeeSelfServicePolicyDTOSchema = z.object({
  policyDocumentId: z.string(),
  policyDocumentVersionId: z.string(),
  policySlug: z.string(),
  title: z.string(),
  version: z.number(),
  contentMarkdown: z.string(),
  plateContent: z.unknown().nullable().optional(),
});

export type EmployeeSelfServicePolicyDTO = z.infer<
  typeof EmployeeSelfServicePolicyDTOSchema
>;

export const EmployeeSelfServiceAccessDTOSchema = z.object({
  employeeId: z.string(),
  hasActiveAccess: z.boolean(),
  url: z.string().nullable(),
  expiresAt: z.coerce.date().nullable(),
  lastSelfServiceAt: z.coerce.date().nullable(),
  lastReminderAt: z.coerce.date().nullable(),
  lastReminderStatus: z.string().nullable(),
});

export type EmployeeSelfServiceAccessDTO = z.infer<typeof EmployeeSelfServiceAccessDTOSchema>;

/** Per-employee posture rollup for the Directory "Compliance" column:
 *  devices / training / policy-acknowledgment counts in one batched read. */
export const EmployeeComplianceOverviewDTOSchema = z.object({
  employeeId: z.string(),
  devices: z.object({ total: z.number(), compliant: z.number() }),
  training: z.object({ assigned: z.number(), completed: z.number() }),
  acknowledgments: z.object({ assigned: z.number(), completed: z.number() }),
});

export type EmployeeComplianceOverviewDTO = z.infer<
  typeof EmployeeComplianceOverviewDTOSchema
>;

export const EmployeeReminderFailureDTOSchema = z.object({
  employeeId: z.string(),
  email: z.string(),
  error: z.string(),
});

export type EmployeeReminderFailureDTO = z.infer<typeof EmployeeReminderFailureDTOSchema>;

export const EmployeeReminderDispatchResultDTOSchema = z.object({
  requested: z.number(),
  linksPrepared: z.number(),
  emailsSent: z.number(),
  failures: z.array(EmployeeReminderFailureDTOSchema),
});

export type EmployeeReminderDispatchResultDTO = z.infer<
  typeof EmployeeReminderDispatchResultDTOSchema
>;

// ===== Employee Link Candidate DTOs =====

export const EmployeeLinkCandidateDTOSchema = z.object({
  employeeId: z.string(),
  employeeEmail: z.string(),
  employeeName: z.string(),
  userId: z.string(),
  userEmail: z.string(),
  userName: z.string().nullable(),
  reason: z.string(),
});

export type EmployeeLinkCandidateDTO = z.infer<typeof EmployeeLinkCandidateDTOSchema>;

export const EmployeeAccountLinkCandidateDTOSchema = z.object({
  userId: z.string(),
  userEmail: z.string(),
  userName: z.string().nullable(),
  userImage: z.string().nullable(),
  emailVerified: z.boolean(),
  workspaceRole: WorkspaceRoleSchema,
  isExactEmailMatch: z.boolean(),
  linkedEmployeeId: z.string().nullable(),
  linkedEmployeeName: z.string().nullable(),
  unavailableReason: z.enum(['ALREADY_LINKED', 'AUDITOR']).nullable(),
});

export type EmployeeAccountLinkCandidateDTO = z.infer<
  typeof EmployeeAccountLinkCandidateDTOSchema
>;

export const EmployeeAccountLinkCandidatesDTOSchema = z.object({
  employeeId: z.string(),
  employeeEmail: z.string(),
  candidates: z.array(EmployeeAccountLinkCandidateDTOSchema),
});

export type EmployeeAccountLinkCandidatesDTO = z.infer<
  typeof EmployeeAccountLinkCandidatesDTOSchema
>;

export const EmployeeLinkPreviewDTOSchema = z.object({
  safeMatches: z.array(EmployeeLinkCandidateDTOSchema),
});

export type EmployeeLinkPreviewDTO = z.infer<typeof EmployeeLinkPreviewDTOSchema>;

export const EmployeeComplianceDashboardDTOSchema = z.object({
  employee: EmployeeDTOSchema,
  pendingPolicies: z.array(EmployeeSelfServicePolicyDTOSchema),
  completedAcknowledgments: z.array(EmployeeAcknowledgmentDTOSchema),
  trainingRecords: z.array(TrainingRecordDTOSchema),
  devices: z.array(EmployeeDeviceDTOSchema),
  selfServiceAccessStatus: EmployeeSelfServiceAccessDTOSchema.nullable(),
});

export type EmployeeComplianceDashboardDTO = z.infer<
  typeof EmployeeComplianceDashboardDTOSchema
>;

export const EmployeeMyComplianceDashboardDTOSchema = z.object({
  status: z.enum(['linked', 'not_linked']),
  dashboard: EmployeeComplianceDashboardDTOSchema.nullable(),
});

export type EmployeeMyComplianceDashboardDTO = z.infer<
  typeof EmployeeMyComplianceDashboardDTOSchema
>;

// ===== People Stats DTO (for dashboard) =====

export const TrainingCompletionStatsDTOSchema = z.object({
  programId: z.string(),
  programName: z.string(),
  required: z.boolean(),
  totalEmployees: z.number(),
  completed: z.number(),
  inProgress: z.number(),
  notStarted: z.number(),
  overdue: z.number(),
  waived: z.number(),
  completionRate: z.number(),
});

export type TrainingCompletionStatsDTO = z.infer<typeof TrainingCompletionStatsDTOSchema>;

export const PeopleStatsDTOSchema = z.object({
  /** Total employees in the directory */
  totalEmployees: z.number(),
  /** Breakdown by status */
  byStatus: z.record(z.number()),
  /** Breakdown by type */
  byType: z.record(z.number()),
  /** Breakdown by department */
  byDepartment: z.record(z.number()),

  /** Training summary */
  training: z.object({
    totalPrograms: z.number(),
    requiredPrograms: z.number(),
    overallCompletionRate: z.number(),
    byProgram: z.array(TrainingCompletionStatsDTOSchema),
  }),

  /** Access review summary */
  accessReviews: z.object({
    total: z.number(),
    pending: z.number(),
    inProgress: z.number(),
    completed: z.number(),
    /** Most recent completed review date */
    lastCompletedAt: z.coerce.date().nullable(),
  }),

  /** Device compliance summary */
  devices: z.object({
    total: z.number(),
    compliant: z.number(),
    nonCompliant: z.number(),
    unknown: z.number(),
    pendingCheck: z.number(),
    complianceRate: z.number(),
  }),
});

export type PeopleStatsDTO = z.infer<typeof PeopleStatsDTOSchema>;

/**
 * #1995 — the org chart is a view of the People roster (active, in-scope
 * people). Deliberately slim: every workspace member, EMPLOYEE included, can
 * read it in the Employee Center, so no email or HR fields.
 */
export const OrgChartNodeDTOSchema = z.object({
  id: z.string(),
  name: z.string(),
  title: z.string().nullable(),
  department: z.string().nullable(),
  type: EmployeeTypeSchema,
  /** Set only when the manager is also on the chart. */
  managerEmployeeId: z.string().nullable(),
  holdsSecurityRole: z.boolean(),
});
export type OrgChartNodeDTO = z.infer<typeof OrgChartNodeDTOSchema>;

export const OrgChartGapReasonSchema = z.enum([
  /** No manager recorded. */
  'NO_MANAGER',
  /** The manager is terminated or excluded from the roster. */
  'MANAGER_INACTIVE',
  /** The manager is a free-text name (IdP/HRIS sync) not linked to a person. */
  'MANAGER_UNLINKED',
]);
export type OrgChartGapReason = z.infer<typeof OrgChartGapReasonSchema>;

export const OrgChartDTOSchema = z.object({
  nodes: z.array(OrgChartNodeDTOSchema),
  /** The person at the top: the root with the most people under them. */
  topEmployeeId: z.string().nullable(),
  gaps: z.object({
    /** Roots other than the top — people not placed under anyone. */
    unplaced: z.array(z.object({
      employeeId: z.string(),
      reason: OrgChartGapReasonSchema,
      managerName: z.string().nullable(),
    })),
    /** Each cycle as the employee ids in reporting order. */
    cycles: z.array(z.array(z.string())),
    noSecurityRoleHolder: z.boolean(),
  }),
});
export type OrgChartDTO = z.infer<typeof OrgChartDTOSchema>;

export interface AccessReviewWorkforceImpactDTO {
  recoveryPending: boolean;
  fingerprint: string;
  checkedGrants: number;
  changes: Array<{ employeeId: string; email: string; reasons: string[] }>;
  affectedGrants: Array<{ reviewId: string; itemId: string; application: string; account: string; previousDecision: string; reasons: string[] }>;
}
